import { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '../controls/Button';
import { UserContext } from '../context/user.context';
import { useData } from '../context/data.context';
import CustomInputBox from '../controls/CustomInputBox';
import { updateUser } from '../services/dashboard-service';
import { UpdateUserAttribute } from '../constants/telosair-interfaces';
import ChangePasswordModal from '../modals/ChangePassword';
import { ROLE_BY_ID } from '../constants/global-constants';
/**
 * Formdata.
 */
interface FORMDATA {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

type ActionElementProps = {
  label: string;
  actionElement: ReactElement;
};

/**
 * Component to display each action in profile page with label.
 * @param props - Properties of ActionElement components.
 * @returns - React element representing ActionElement component.
 */
function ActionElement(props: ActionElementProps): ReactElement {
  const { label, actionElement } = props;
  return (
    <div className="flex w-full mt-8">
      <div className="text-sm font-medium text-gray-500 w-1/5">{label}</div>
      {actionElement}
    </div>
  );
}

/**
 * Profile component to edit the current profile.
 * @returns  A React element representing the Profile component.
 */
export default function Profile(): ReactElement {
  const { currentUser, fetchAllUserList } = useData();
  const { profile_picture: profilePicture } = useContext(UserContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { state } = useLocation();
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword] = useState(false);
  const navigate = useNavigate();

  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: useMemo(() => {
      return {
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
        email: currentUser?.userName,
        password: 'notAPasswordLoL!'
      };
    }, [currentUser])
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset({
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      email: currentUser?.userName
    });
  }, [currentUser]);

  useEffect(() => {
    if (state?.change) {
      setResetPasswordModal(true);
    }
  }, [state]);

  /**
   * Handles the Submit.
   * @param data -The list used to get data.
   */
  const onhandleSubmit = async (data: FORMDATA): Promise<void> => {
    try {
      if (currentUser?.id) {
        const payload: UpdateUserAttribute = {
          id: currentUser.id,
          status: currentUser.status,
          userName: currentUser.userName,
          role: currentUser.role,
          clientId: currentUser.clientId,
          ...data
        };
        setLoading(true);
        const updateResponse = await updateUser(currentUser.id.toString(), payload);
        if (updateResponse.data) {
          fetchAllUserList();
          toast.success('Profile updated successfully.');
        } else {
          toast.error('Failed to update user data');
        }
        setLoading(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      setLoading(false);
      toast.error('Failed to update user data');
    }
  };
  return (
    <div className="my-6 mx-2 p-6 flex flex-1 flex-col bg-container-500 rounded">
      <div className="flex flex-1 flex-col">
        <div className="text-white text-lg font-semibold">Profile</div>
        <div className="flex flex-1 flex-col border-t mt-6 pt-8 border-t-gray-900">
          <div className="flex w-2/3">
            <div className="text-sm font-medium text-gray-500 w-1/5">Display Picture</div>
            <div className="bg-white/10 rounded-full h-20 w-20 relative">
              <img
                src={profilePicture}
                className={`hover:ring-4 ring-background-700 h-full w-full rounded-full `}
                alt=""
              />
              <div className="bg-primary-500 rounded-full text-[0.625rem] text-white h-5 w-5 justify-center items-center material-icons-outlined absolute bottom-0 right-0 flex">
                edit
              </div>
            </div>
          </div>
          {/* eslint-disable-next-line */}
          <FormProvider {...methods}>
            <form className="w-full" onSubmit={handleSubmit(onhandleSubmit)}>
              <div className="w-2/3">
                <ActionElement
                  label="Role"
                  actionElement={
                    <div className="bg-gray-800 rounded py-3 px-4 text-white text-sm font-medium">
                      {currentUser?.role ? ROLE_BY_ID[currentUser?.role] : '-'}
                    </div>
                  }
                />
                <ActionElement
                  label="First Name"
                  actionElement={
                    <div className="w-1/2">
                      <CustomInputBox
                        label=""
                        noMarginTop
                        idname="firstName"
                        type="given-name"
                        validationObject={{ required: 'First name is required' }}
                      />
                    </div>
                  }
                />
                <ActionElement
                  label="Last Name"
                  actionElement={
                    <div className="w-1/2">
                      <CustomInputBox
                        label=""
                        noMarginTop
                        type="family-name"
                        idname="lastName"
                        validationObject={{ required: 'Last name is required' }}
                      />
                    </div>
                  }
                />
                <ActionElement
                  label="Email ID"
                  actionElement={
                    <div className="w-1/2">
                      <CustomInputBox idname="email" isDisabled autoComplete="email" noMarginTop />
                    </div>
                  }
                />
                <ActionElement
                  label="Password"
                  actionElement={
                    <div className="w-1/2">
                      <CustomInputBox
                        idname="password"
                        isDisabled
                        autoComplete="current-password"
                        type={showPassword ? 'text' : 'password'}
                        actionElement={
                          <button
                            className="absolute right-2 bg-gray-800 flex items-center text-primary-200 top-2 bottom-2 text-xs px-2 rounded"
                            type="button"
                            onClick={() => setResetPasswordModal(true)}
                          >
                            <div>Change</div>
                          </button>
                        }
                        noMarginTop
                      />
                    </div>
                  }
                />
                {/* <ActionElement
                label="Phone No."
                actionElement={
                  <div className="w-1/2">
                    <InputBox
                      name="phone"
                      id="phone"
                      autoComplete="tel"
                      noMarginTop
                      textValue={phone}
                      setText={setPhone}
                    />
                  </div>
                }
              /> */}
              </div>
              <div className="flex justify-end">
                <div>
                  <Button
                    loading={loading}
                    buttonType="submit"
                    theme="primary"
                    size="md"
                    name="Save Changes"
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
      {resetPasswordModal && (
        <ChangePasswordModal
          onForgetPass={() => {
            setResetPasswordModal(false);
            navigate('/auth/forgot-password');
          }}
          handleClose={() => {
            setResetPasswordModal(false);
          }}
        />
      )}
    </div>
  );
}
